import { ListItem } from 'aos-ds/src/primitives/listitem/ListItem'
import { listitemContract } from 'aos-ds/src/primitives/listitem/listitem.contract'
import { spacing } from 'aos-ds/src/tokens/spacing'
import React, { useCallback } from 'react'
import { View } from 'react-native'

import { withoutSampleWidth } from '../../tailwind/withoutSampleWidth'
import { Popover, PopoverBackdrop, PopoverContent } from '../popover'
import { Pressable } from '../pressable'
import { resolveCollapsed } from './filterSwitchContracts'
import { FilterSwitchTrigger } from './FilterSwitchTrigger'
import type { SwitchProps } from './types'

const LIST_OFFSET = spacing['small-compact/xs']

const LIST_PART = 'slot'

const listSurfaceClass = (): string => {
    const part = resolveCollapsed(true).parts[LIST_PART]
    if (!part) {
        throw new Error(`FilterSwitchRow: FilterSwitch/Collapsed open=true has no "${LIST_PART}"`)
    }
    return withoutSampleWidth(part)
}

const LIST_ROW = 'variant=default, size=small'

const LIST_ROW_LEAD = (() => {
    const part = (listitemContract.variants as Record<string, { parts: Record<string, string> }>)[
        LIST_ROW
    ]?.parts.line
    if (!part) {
        throw new Error(`FilterSwitchRow: ListItem "${LIST_ROW}" has no "line" part`)
    }
    return part
})()

const LIST_ROW_WIDTH = 'w-full hover:w-full'

type CollapsedProps = SwitchProps & {
    open: boolean
    onOpenChange(open: boolean): void
}

export const FilterSwitchCollapsed = ({ group, open, onOpenChange, testID }: CollapsedProps) => {
    const close = useCallback(() => onOpenChange(false), [onOpenChange])
    return (
        <View className={withoutSampleWidth(resolveCollapsed(open).container)} testID={testID}>
            <Popover
                isOpen={open}
                onClose={close}
                trapFocus={false}
                focusScope={false}
                placement='bottom left'
                offset={LIST_OFFSET}
                trigger={triggerProps => (
                    <Pressable
                        {...triggerProps}
                        accessibilityRole='button'
                        accessibilityState={{ expanded: open }}
                        onPress={() => onOpenChange(!open)}
                        className='web:cursor-pointer'
                        testID={`${testID}-trigger`}
                    >
                        <FilterSwitchTrigger group={group} open={open} testID={`${testID}-face`} />
                    </Pressable>
                )}
            >
                <PopoverBackdrop />
                <PopoverContent className={listSurfaceClass()} testID={`${testID}-list`}>
                    {group.options.map(option => (
                        <Pressable
                            key={String(option.value)}
                            accessibilityRole='menuitem'
                            accessibilityState={{ selected: option.value === group.value }}
                            onPress={() => {
                                group.onChange(option.value)
                                close()
                            }}
                            className='self-stretch web:cursor-pointer'
                            testID={`${testID}-option-${String(option.value)}`}
                        >
                            <ListItem
                                size='small'
                                leading={
                                    <View
                                        className={LIST_ROW_LEAD}
                                        testID={`${testID}-option-lead`}
                                    />
                                }
                                className={LIST_ROW_WIDTH}
                            >
                                {option.label}
                            </ListItem>
                        </Pressable>
                    ))}
                </PopoverContent>
            </Popover>
        </View>
    )
}
